import { useEffect, useState } from 'react';
import { SERVER_ORIGIN } from '../config';

interface TelemetryEvent {
  id: string;
  userId: string | null;
  username: string | null;
  visitorId: string;
  sessionId: string;
  eventName: string;
  payload: any;
  createdAt: string;
}

interface ClientError {
  id: string;
  userId: string | null;
  username: string | null;
  message: string;
  stack: string | null;
  url: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface DashboardData {
  funnel: Record<string, number>;
  runs: Record<string, number>;
  matches: Record<string, number>;
  events: TelemetryEvent[];
  errors: ClientError[];
  globalTelemetryEnabled: boolean;
}

export function AdminDashboard({
  token,
  onGoBack,
}: {
  token: string;
  onGoBack: () => void;
}) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [expandedErrorId, setExpandedErrorId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/custom/telemetry/admin-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 403) {
          setError('Access Denied: Admin privileges required.');
        } else {
          setError(`Error fetching dashboard: ${res.statusText}`);
        }
        return;
      }
      const payload = await res.json();
      setData(payload);
      setError(null);
    } catch (err: any) {
      setError(`Network error: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleToggle = async () => {
    if (!data || toggling) return;
    setToggling(true);
    const targetStatus = !data.globalTelemetryEnabled;
    try {
      const res = await fetch(`${SERVER_ORIGIN}/custom/telemetry/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: targetStatus }),
      });
      if (!res.ok) {
        throw new Error(`Toggle failed: ${res.statusText}`);
      }
      setData((prev) => prev ? { ...prev, globalTelemetryEnabled: targetStatus } : null);
    } catch (err: any) {
      alert(err.message || 'Failed to toggle telemetry');
    } finally {
      setToggling(false);
    }
  };

  if (loading && !data) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Initializing Secure Connection...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>ACCESS RESTRICTED</h2>
        <p style={styles.errorText}>{error}</p>
        <button onClick={onGoBack} className="admin-btn" style={styles.backButton}>Return to Lobby</button>
      </div>
    );
  }

  if (!data) return null;

  // Compute funnel conversion percentages
  const funnel = data.funnel || {};
  const landingCount = funnel['funnel_landing'] || 0;
  const tutStartCount = funnel['funnel_tutorial_start'] || 0;
  const tutCompleteCount = funnel['funnel_tutorial_complete'] || 0;
  const regPromptCount = funnel['funnel_register_prompt'] || 0;
  const regSuccessCount = funnel['funnel_register_success'] || 0;

  const getPercentage = (count: number) => {
    if (landingCount === 0) return '0%';
    return `${Math.round((count / landingCount) * 100)}%`;
  };

  return (
    <div style={styles.container}>
      <style>{`
        .admin-row {
          transition: background-color 0.2s ease;
        }
        .admin-row:hover {
          background-color: #1a1a22 !important;
        }
        .admin-btn {
          transition: all 0.2s ease;
        }
        .admin-btn:hover {
          filter: brightness(1.15);
          box-shadow: 0 0 8px rgba(0, 255, 163, 0.2);
        }
        .admin-btn:active {
          transform: scale(0.98);
        }
        .admin-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #1c1c1f;
          border-top: 4px solid #00ffa3;
          borderRadius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>🔒 GRANDSNATCH ADMIN CONSOLE</h1>
          <p style={styles.subtitle}>System status & real-time telemetry analytics</p>
        </div>
        <div style={styles.headerControls}>
          <button onClick={fetchData} disabled={loading} className="admin-btn" style={styles.refreshButton}>
            {loading ? 'Refreshing...' : '🔄 Refresh Data'}
          </button>
          <button onClick={onGoBack} className="admin-btn" style={styles.backButton}>
            ⬅ Exit Console
          </button>
        </div>
      </header>

      {/* Control Panel: Kill Switch */}
      <section style={styles.controlPanel}>
        <div style={styles.switchInfo}>
          <h2 style={styles.sectionTitle}>Global Telemetry Switch</h2>
          <p style={styles.sectionDesc}>
            Enable or disable server-side telemetry event capture and client-side reporting globally.
          </p>
        </div>
        <div>
          <button
            onClick={handleToggle}
            disabled={toggling}
            className="admin-btn"
            style={{
              ...styles.toggleButton,
              backgroundColor: data.globalTelemetryEnabled ? '#1b5e20' : '#b71c1c',
              borderColor: data.globalTelemetryEnabled ? '#00e676' : '#ff1744',
            }}
          >
            <span style={{
              ...styles.toggleIndicator,
              color: data.globalTelemetryEnabled ? '#00ffa3' : '#ff1744'
            }}>
              {data.globalTelemetryEnabled ? '●' : '○'}
            </span>
            {data.globalTelemetryEnabled ? 'SYSTEM RECORDING ACTIVE' : 'SYSTEM MUTED / DISABLED'}
          </button>
        </div>
      </section>

      <div style={styles.dashboardGrid}>
        {/* Funnel Section */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Registration Conversion Funnel</h3>
          <div style={styles.funnelContainer}>
            <div style={styles.funnelStep}>
              <div style={styles.funnelLabel}>
                <span>Landing page visitors</span>
                <span style={styles.funnelCount}>{landingCount}</span>
              </div>
              <div style={styles.progressBarBg}>
                <div style={{ ...styles.progressBarFill, width: '100%', backgroundColor: '#00e5ff' }} />
              </div>
              <div style={styles.funnelSubtext}>Baseline: 100%</div>
            </div>

            <div style={styles.funnelStep}>
              <div style={styles.funnelLabel}>
                <span>Tutorial Started</span>
                <span style={styles.funnelCount}>{tutStartCount}</span>
              </div>
              <div style={styles.progressBarBg}>
                <div style={{ ...styles.progressBarFill, width: getPercentage(tutStartCount), backgroundColor: '#00ffa3' }} />
              </div>
              <div style={styles.funnelSubtext}>Conversion: {getPercentage(tutStartCount)} of total</div>
            </div>

            <div style={styles.funnelStep}>
              <div style={styles.funnelLabel}>
                <span>Tutorial Completed</span>
                <span style={styles.funnelCount}>{tutCompleteCount}</span>
              </div>
              <div style={styles.progressBarBg}>
                <div style={{ ...styles.progressBarFill, width: getPercentage(tutCompleteCount), backgroundColor: '#b3e5fc' }} />
              </div>
              <div style={styles.funnelSubtext}>Conversion: {getPercentage(tutCompleteCount)} of total</div>
            </div>

            <div style={styles.funnelStep}>
              <div style={styles.funnelLabel}>
                <span>Register Prompt Triggered</span>
                <span style={styles.funnelCount}>{regPromptCount}</span>
              </div>
              <div style={styles.progressBarBg}>
                <div style={{ ...styles.progressBarFill, width: getPercentage(regPromptCount), backgroundColor: '#ffd740' }} />
              </div>
              <div style={styles.funnelSubtext}>Conversion: {getPercentage(regPromptCount)} of total</div>
            </div>

            <div style={styles.funnelStep}>
              <div style={styles.funnelLabel}>
                <span>Registration Success</span>
                <span style={styles.funnelCount}>{regSuccessCount}</span>
              </div>
              <div style={styles.progressBarBg}>
                <div style={{ ...styles.progressBarFill, width: getPercentage(regSuccessCount), backgroundColor: '#e040fb' }} />
              </div>
              <div style={styles.funnelSubtext}>Conversion: {getPercentage(regSuccessCount)} of total</div>
            </div>
          </div>
        </div>

        {/* System & Run Metrics */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Run & Match Statistics</h3>

          <h4 style={styles.subHeader}>Player Run Campaigns</h4>
          <div style={styles.statsGrid}>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Active Runs</span>
              <span style={{ ...styles.statVal, color: '#00ffa3' }}>{data.runs['active'] || 0}</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Extracted Runs</span>
              <span style={{ ...styles.statVal, color: '#00e5ff' }}>{data.runs['extracted'] || 0}</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Failed Runs</span>
              <span style={{ ...styles.statVal, color: '#ff5252' }}>{data.runs['failed'] || 0}</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Abandoned Runs</span>
              <span style={{ ...styles.statVal, color: '#ffb300' }}>{data.runs['abandoned'] || 0}</span>
            </div>
          </div>

          <h4 style={styles.subHeader}>PvE Gameplay Matches</h4>
          <div style={styles.statsGrid}>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Matches Won</span>
              <span style={{ ...styles.statVal, color: '#00ffa3' }}>{data.matches['won'] || 0}</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Matches Lost</span>
              <span style={{ ...styles.statVal, color: '#ff5252' }}>{data.matches['lost'] || 0}</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Matches Drawn</span>
              <span style={{ ...styles.statVal, color: '#e0e0e0' }}>{data.matches['draw'] || 0}</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>Total Played</span>
              <span style={{ ...styles.statVal, color: '#b3e5fc' }}>
                {(data.matches['won'] || 0) + (data.matches['lost'] || 0) + (data.matches['draw'] || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Errors Console */}
      <section style={styles.card}>
        <h3 style={styles.cardTitle}>🚨 Recent Client Crash Errors (Last 10)</h3>
        {data.errors.length === 0 ? (
          <p style={styles.emptyText}>No client errors reported.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Timestamp</th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Message</th>
                  <th style={styles.th}>URL</th>
                  <th style={styles.th}>User Agent</th>
                </tr>
              </thead>
              <tbody>
                {data.errors.map((err) => (
                  <tr
                    key={err.id}
                    onClick={() => setExpandedErrorId(expandedErrorId === err.id ? null : err.id)}
                    className="admin-row"
                    style={styles.clickableRow}
                  >
                    <td style={styles.td}>{new Date(err.createdAt).toLocaleTimeString()}</td>
                    <td style={styles.td}>{err.username || <span style={styles.anonymous}>Anonymous</span>}</td>
                    <td style={{ ...styles.td, color: '#ff5252', fontWeight: 'bold' }}>{err.message}</td>
                    <td style={styles.td}>{err.url || '-'}</td>
                    <td style={styles.td}>{err.userAgent ? err.userAgent.substring(0, 45) + '...' : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {expandedErrorId && (
          <div style={styles.detailsPanel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={styles.detailsTitle}>Error Stack Trace & Details</h4>
              <button
                onClick={() => setExpandedErrorId(null)}
                style={styles.closeDetailsBtn}
              >✕ Close</button>
            </div>
            <pre style={styles.pre}>
              {JSON.stringify(
                data.errors.find((e) => e.id === expandedErrorId),
                null,
                2
              )}
            </pre>
          </div>
        )}
      </section>

      {/* Events Stream */}
      <section style={styles.card}>
        <h3 style={styles.cardTitle}>📡 Live Telemetry Feed (Last 20 Events)</h3>
        {data.events.length === 0 ? (
          <p style={styles.emptyText}>No events recorded in this database.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Timestamp</th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Event Name</th>
                  <th style={styles.th}>Visitor ID</th>
                  <th style={styles.th}>Session ID</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((evt) => (
                  <tr
                    key={evt.id}
                    onClick={() => setExpandedEventId(expandedEventId === evt.id ? null : evt.id)}
                    className="admin-row"
                    style={styles.clickableRow}
                  >
                    <td style={styles.td}>{new Date(evt.createdAt).toLocaleTimeString()}</td>
                    <td style={styles.td}>{evt.username || <span style={styles.anonymous}>Anonymous</span>}</td>
                    <td style={{ ...styles.td, color: '#00ffa3', fontWeight: 'bold' }}>{evt.eventName}</td>
                    <td style={styles.td}>{evt.visitorId.substring(0, 8)}...</td>
                    <td style={styles.td}>{evt.sessionId.substring(0, 8)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {expandedEventId && (
          <div style={styles.detailsPanel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ ...styles.detailsTitle, color: '#00ffa3' }}>Telemetry Event Payload</h4>
              <button
                onClick={() => setExpandedEventId(null)}
                style={styles.closeDetailsBtn}
              >✕ Close</button>
            </div>
            <pre style={{ ...styles.pre, color: '#e0f2f1' }}>
              {JSON.stringify(
                data.events.find((e) => e.id === expandedEventId),
                null,
                2
              )}
            </pre>
          </div>
        )}
      </section>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#0a0a0c',
    color: '#eee',
    fontFamily: 'monospace',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '4px solid #1c1c1f',
    borderTop: '4px solid #00ffa3',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: 20,
  },
  loadingText: {
    fontSize: 14,
    letterSpacing: '1px',
    color: '#00ffa3',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#1a0a0a',
    color: '#ff5252',
    fontFamily: 'monospace',
    textAlign: 'center',
    padding: 20,
  },
  errorTitle: {
    fontSize: 24,
    letterSpacing: '2px',
    marginBottom: 10,
    color: '#ff1744',
  },
  errorText: {
    color: '#ff8a80',
    fontSize: 14,
    marginBottom: 20,
  },
  container: {
    padding: '24px',
    backgroundColor: '#0a0a0d',
    height: '100%',
    overflowY: 'auto',
    color: '#e0e0e3',
    fontFamily: '"Outfit", "Inter", sans-serif',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    borderBottom: '1px solid #1e1e24',
    paddingBottom: '16px',
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ffffff',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9e9e9e',
    margin: '4px 0 0 0',
  },
  headerControls: {
    display: 'flex',
    gap: '12px',
  },
  refreshButton: {
    backgroundColor: '#1c1c21',
    color: '#fff',
    border: '1px solid #2a2a32',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  backButton: {
    backgroundColor: '#b71c1c',
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  controlPanel: {
    backgroundColor: '#111115',
    border: '1px solid #22222a',
    borderRadius: '8px',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: 16,
  },
  switchInfo: {
    flex: '1 1 300px',
  },
  sectionTitle: {
    fontSize: '16px',
    color: '#fff',
    margin: 0,
  },
  sectionDesc: {
    fontSize: '12px',
    color: '#8e8e93',
    margin: '4px 0 0 0',
  },
  toggleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#ffffff',
    border: '1px solid',
    padding: '12px 20px',
    borderRadius: '6px',
    fontWeight: 'bold',
    fontSize: '14px',
    cursor: 'pointer',
  },
  toggleIndicator: {
    fontSize: '16px',
    marginRight: '6px',
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '24px',
    marginBottom: '24px',
  },
  card: {
    backgroundColor: '#111115',
    border: '1px solid #22222a',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
  },
  cardTitle: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#fff',
    margin: '0 0 16px 0',
    borderBottom: '1px solid #22222a',
    paddingBottom: '8px',
  },
  funnelContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  funnelStep: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  funnelLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#eee',
  },
  funnelCount: {
    fontWeight: 'bold',
  },
  progressBarBg: {
    backgroundColor: '#22222a',
    height: '8px',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease-out',
  },
  funnelSubtext: {
    fontSize: '11px',
    color: '#8a8a8f',
  },
  subHeader: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#9e9e9e',
    margin: '16px 0 8px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
  },
  statBox: {
    backgroundColor: '#17171c',
    border: '1px solid #282830',
    borderRadius: '6px',
    padding: '12px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statLabel: {
    fontSize: '11px',
    color: '#8e8e93',
    textAlign: 'center',
  },
  statVal: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginTop: '4px',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    textAlign: 'left',
  },
  th: {
    borderBottom: '1px solid #22222a',
    padding: '8px',
    color: '#8e8e93',
    fontWeight: 'bold',
  },
  td: {
    borderBottom: '1px solid #1c1c22',
    padding: '8px',
    verticalAlign: 'top',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  anonymous: {
    color: '#6e6e73',
    fontStyle: 'italic',
  },
  emptyText: {
    fontSize: '13px',
    color: '#6e6e73',
    textAlign: 'center',
    margin: '16px 0',
  },
  detailsPanel: {
    marginTop: '16px',
    backgroundColor: '#07070a',
    border: '1px solid #33333f',
    borderRadius: '6px',
    padding: '16px',
  },
  detailsTitle: {
    color: '#ff5252',
    margin: 0,
    fontSize: '14px',
    fontWeight: 'bold',
  },
  closeDetailsBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#8e8e93',
    cursor: 'pointer',
    fontSize: '12px',
  },
  pre: {
    margin: 0,
    fontFamily: 'monospace',
    fontSize: '12px',
    overflowX: 'auto',
    color: '#ffcdd2',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
};
